import { useQuery } from '@apollo/client';
import { LoadingError } from 'components/loadingError';
import {
  GET_APP_VERSION,
  QueryGetAppVersionResponse,
} from 'graphql/client/general';
import { AppLayoutProps } from 'layouts/application/types';
import React, { ReactElement, useEffect } from 'react';
import { matchPath, Redirect, useHistory, useLocation } from 'react-router-dom';
import {
  UserRoleKey,
  useUpdateSessionAppVersionMutation,
} from 'apollo/generated/client-operations';
import { isSsr } from 'utils';
import { AuthContext } from 'auth';
import { useFlags } from 'featureFlags';
import { PolicyConsentGate, isPolicyAccepted } from 'containers/policyConsent';
import { AppLayoutContent } from './AppLayoutContent';

export function AppLayout(props: AppLayoutProps): ReactElement {
  const location = useLocation();
  const history = useHistory();
  const authContext = React.useContext(AuthContext);
  const { currentSession, currentUser, authState } = authContext ?? {};
  const { session, policyConsentStatuses = [] } = currentSession ?? {};
  const { urlLock } = session ?? {};
  const { consentRecaptureGateEnabled } = useFlags();
  const hasOutstandingPolicyConsent = policyConsentStatuses.some(
    (status) => !isPolicyAccepted(status),
  );
  // The consent gate is only pending when the user reached the app through the
  // login process (see `signIn`); it is never set for a user who is already
  // logged in and navigating an existing session (sc-16853). The marker is
  // persisted, so refreshing the page keeps the gate up until the user accepts
  // or signs out.
  const consentGatePending = authState?.getConsentGatePending() ?? false;

  const { data: appVersionData } =
    useQuery<QueryGetAppVersionResponse>(GET_APP_VERSION);
  const [updateSessionAppVersion] = useUpdateSessionAppVersionMutation();

  useEffect(() => {
    // Update the session with the current app version if the version is out-of-date
    if (
      session &&
      appVersionData?.appVersion &&
      session.appVersion !== appVersionData.appVersion
    ) {
      updateSessionAppVersion({
        variables: { appVersion: appVersionData.appVersion },
      });
    }
  }, [session]);

  useEffect(() => {
    // Once the signed-in user has nothing outstanding to consent to, retire the
    // pending marker. This keeps its lifetime bounded to the login it was set
    // for, so a later policy change never gates an already-logged-in user
    // mid-session — they only see the gate the next time they log in.
    if (session && !hasOutstandingPolicyConsent && consentGatePending) {
      authState?.clearConsentGatePending();
    }
  }, [session, hasOutstandingPolicyConsent, consentGatePending]);

  const { route } = props;

  if (!session) {
    if (isSsr()) {
      return <LoadingError loading />;
    }
    return <Redirect push to="/auth/login" />;
  }

  if (!currentUser?.hasRole(UserRoleKey.Worker) && !currentUser.userAccountId) {
    return <Redirect to="/mobile-only" />;
  }

  if (urlLock && history?.location?.pathname !== urlLock) {
    return <Redirect to={urlLock} />;
  }

  // Platform policy re-capture gate: block all authenticated routes behind an
  // unskippable consent screen while the user has outstanding policy consents.
  // Shown only when the gate is pending for this login (never mid-session for an
  // already-logged-in user, sc-16853); once shown it stays up across refreshes
  // until the user accepts or signs out. Gated by its own contextless kill
  // switch (falsy until LD resolves / on SSR, which safely keeps the gate off).
  if (
    consentRecaptureGateEnabled &&
    hasOutstandingPolicyConsent &&
    consentGatePending
  ) {
    return <PolicyConsentGate policyConsentStatuses={policyConsentStatuses} />;
  }

  const currentRoute = route?.routes.find((mappedRoute) => {
    const match = matchPath(location.pathname, { path: mappedRoute.path });
    return match?.isExact;
  });

  return (
    <AppLayoutContent
      routes={route.routes}
      currentRoute={currentRoute}
      history={history}
      pathName={location.pathname}
      currentSession={currentSession}
    />
  );
}
